/**
 * Etna's YANARDAĞ — the volcano — in the shared 120×120 LINE asset box.
 *
 * The Home tab and all four passcode dots draw the mountain. The chat wallpaper
 * drifts EMBERS off it, which is the same relationship Sakura's petals have with
 * its blossom: a piece of the hero rather than a copy of it.
 *
 * WHY THIS IS NOT EVEREST'S SUMMIT AGAIN
 *
 * This family already has a mountain, and a second one has to differ in
 * SILHOUETTE, not merely in colour. Everest is a PEAK: a sharp apex with snowcaps,
 * drawn as a triangle with a bite out of the top. This is a CONE, and every
 * difference is structural:
 *
 *   the top is TRUNCATED — a crater rim rather than a point, with a notch in it;
 *   the flanks are CONCAVE, sweeping out to a wide base the way a stratovolcano
 *     builds itself, where a rock peak's are straight or convex;
 *   there is a PLUME, which is the only thing in this whole set that is not solid;
 *   and it is LIT FROM INSIDE.
 *
 * WHAT IS LOAD-BEARING
 *
 *  - THE TRUNCATED TOP — a THIRTY-TWO unit rim, dished in the middle. It is the
 *    whole difference. Bring the flanks to a point and this is a mountain that
 *    happens to be on fire.
 *  - THE CONCAVE FLANKS. Straight ones read as a slag heap; the sweep is what says
 *    the thing built itself out of what it threw.
 *  - THE LAVA COMES OVER THE LIP. A glow that stops short of the top is a fire on
 *    a mountain; one that comes over the edge is the mountain's own.
 *  - THE PLUME IS OFF-CENTRE and leans. Straight up it reads as a chimney.
 *
 * WHAT THE REDRAW FIXED (owner: careless)
 *
 * Three things, and none of them was a matter of taste.
 *
 *  - THE RIM WAS A NOTCH, not a crater. Twenty-two units wide with a ten-unit dip
 *    bitten into it — about four pixels at tab size. What came back at 44px was a
 *    smooth two-humped hill, which is the one thing this glyph had to avoid, since
 *    the set already contains Everest and Fuji. The rim is now half again as wide
 *    and DISHED across its whole span, so the flat top survives to 22px.
 *  - THE PLUME WAS A LUMP. Three puffs widening to sixteen units, painted in the
 *    tab colour — which on this theme's passcode ramp is a mid BROWN — sat over
 *    the crater as a fat dun blob. On a set whose brief is kawaii that was the
 *    single most off-putting shape in the run. It is smaller, rounder, leans
 *    properly, and it is painted in warm ASH rather than in whatever colour the
 *    mountain happens to be.
 *  - THE LAVA WAS A SCRATCH. Two kinked four-unit polygons; at tab size a pale
 *    hairline crack. They are now proper TONGUES with rounded ends — twice the
 *    width, and they spill over the lip rather than starting under it.
 *
 * Every subpath is wound clockwise so a nonzero fill unions them
 * ([[line-shared-glyph-modules]]).
 */

const f = ( v: number ) => +v.toFixed( 1 );

/** A polygon in clockwise order (y down), whichever order it arrives in. */
function cw( pts: [ number, number ][] ): string {
  let area = 0;
  for ( let i = 0; i < pts.length; i++ ) {
    const a = pts[ i ], b = pts[ ( i + 1 ) % pts.length ];
    area += a[ 0 ] * b[ 1 ] - b[ 0 ] * a[ 1 ];
  }
  const p = area < 0 ? pts.slice().reverse() : pts;
  return 'M' + p.map( ( q ) => `${ f( q[ 0 ] ) },${ f( q[ 1 ] ) }` ).join( ' L' ) + ' Z';
}

/* ---------------------------------------------------------------- geometry -- */

/** The base, and the crater rim. */
const FOOT = 108, RIM = 38, RIM_L = 44, RIM_R = 76;
/** How far the rim dishes down in the middle. */
const DISH = 7;

/**
 * The CONE: up the left flank on a concave sweep, across the DISHED rim, and down
 * the right.
 *
 * The flanks are cubics whose control points sit INSIDE the straight line from
 * foot to rim, which is what makes them concave. Drawn straight the mountain is a
 * slag heap; drawn convex it is a hill.
 *
 * The rim is a single shallow curve rather than the bitten notch this glyph
 * carried first. A notch is a detail; a dish is a SILHOUETTE, and a silhouette is
 * all a 22px tab has.
 */
export const VOLCANO_CONE =
  `M6,${ FOOT }`
  + ` C20,${ FOOT - 24 } ${ RIM_L - 9 },${ RIM + 20 } ${ RIM_L },${ RIM }`
  + ` C${ RIM_L + 7 },${ RIM + DISH } ${ RIM_R - 7 },${ RIM + DISH } ${ RIM_R },${ RIM }`
  + ` C${ RIM_R + 9 },${ RIM + 20 } 100,${ FOOT - 24 } 114,${ FOOT } Z`;

/**
 * A lower shoulder on the right, so the mountain has a flank rather than being a
 * free-standing cone on a plain.
 *
 * LOWER than the first pass, which put its apex twenty units up and gave the
 * silhouette a second summit — a two-humped hill, which is exactly what this glyph
 * must not be. Twelve units is a ridge running away from the cone.
 */
export const VOLCANO_SHOULDER = cw( [
  [ 80, FOOT ], [ 97, FOOT - 12 ], [ 116, FOOT - 3 ], [ 118, FOOT ],
] );

/**
 * A TONGUE of lava: a ribbon walked along a centre line with a rounded end.
 *
 * The width barely changes along its length, because lava follows a channel — what
 * changes constantly is its LINE. The rounded end is doing two jobs: it is what a
 * flow front actually looks like, and it is the difference between this reading as
 * molten rock and as a cracked windscreen.
 */
function tongue( pts: [ number, number ][], w: number ): string {
  const N = 48;
  const at = ( t: number ): [ number, number ] => {
    // Catmull-Rom through the given points, so the channel bends smoothly
    const u = t * ( pts.length - 1 );
    const i = Math.min( pts.length - 2, Math.floor( u ) );
    const v = u - i;
    const p0 = pts[ Math.max( 0, i - 1 ) ], p1 = pts[ i ];
    const p2 = pts[ i + 1 ], p3 = pts[ Math.min( pts.length - 1, i + 2 ) ];
    const h = ( a: number, b: number, c: number, d: number ): number =>
      0.5 * ( ( 2 * b ) + ( -a + c ) * v + ( 2 * a - 5 * b + 4 * c - d ) * v * v
        + ( -a + 3 * b - 3 * c + d ) * v * v * v );
    return [ h( p0[ 0 ], p1[ 0 ], p2[ 0 ], p3[ 0 ] ), h( p0[ 1 ], p1[ 1 ], p2[ 1 ], p3[ 1 ] ) ];
  };
  const left: [ number, number ][] = [], right: [ number, number ][] = [];
  for ( let i = 0; i <= N; i++ ) {
    const t = i / N;
    const [ x, y ] = at( t );
    const [ x2, y2 ] = at( Math.min( 1, t + 0.01 ) );
    const dx = x2 - x, dy = y2 - y;
    const len = Math.hypot( dx, dy ) || 1;
    const nx = -dy / len, ny = dx / len;
    // full width for most of the run, then a round cap over the last tenth
    const cap = t > 0.9 ? Math.sqrt( Math.max( 0, 1 - Math.pow( ( t - 0.9 ) / 0.1, 2 ) ) ) : 1;
    const half = w / 2 * ( 1 - t * 0.10 ) * cap;
    left.push( [ x + nx * half, y + ny * half ] );
    right.push( [ x - nx * half, y - ny * half ] );
  }
  return cw( left.concat( right.reverse() ) );
}

/**
 * The FISSURE: the main flow, over the lip just right of centre and down the
 * front.
 *
 * It starts UNDER THE GLOW, not above the rim. Started proud of the lip — the
 * previous pass — its first few units stood outside the mountain's own silhouette,
 * so both tongues grew a red stub poking into the black above the summit. Lava
 * comes out of the crater, and the crater is the bright lens, so the honest place
 * for a tongue to begin is beneath it.
 */
export const VOLCANO_FISSURE = tongue(
  [ [ 65, 44 ], [ 68, 52 ], [ 64, 60 ], [ 69, 73 ] ], 6 );

/**
 * A second, shorter spill down the left flank — one is a crack, two are an
 * eruption.
 *
 * BOTH ARE SHORT. The pass before this ran them from the crater almost to the
 * foot, and two sixty-unit tubes down a dark cone came back as a pair of pink
 * noodles — they outweighed the mountain they were on, and at 22px they were the
 * first thing the eye met. Real lava on this mountain is a bright collar around
 * the summit with tongues creeping out of it, and the collar is what carries the
 * drawing; the tongues only have to say which way it is running.
 *
 * The left one is also SHORTER and set at a different angle. Matched in length and
 * angle the pair stopped reading as two tongues and became one symmetrical object
 * under the crater — the same rabbit-ear failure Madrid's churros had.
 */
export const VOLCANO_RIVER = tongue(
  [ [ 54, 43 ], [ 49, 50 ], [ 51, 59 ] ], 5 );

/**
 * The GLOW in the crater — a lens sitting INSIDE the dish, brighter than the lava
 * that runs out of it. It is what makes the rim read as broken open rather than as
 * a flat top.
 *
 * Its top edge FOLLOWS the dish rather than cutting straight across it. Drawn flat
 * the lens rose above the rim curve at both ends and poked out past the cone's
 * shoulders — a pale wing either side of the summit, which at 44px read as a bow
 * tie balanced on the mountain.
 */
export const VOLCANO_GLOW =
  `M48,${ RIM + 2 }`
  + ` C54,${ RIM + DISH } 66,${ RIM + DISH } 72,${ RIM + 2 }`
  + ` C66,${ RIM + DISH + 6 } 54,${ RIM + DISH + 6 } 48,${ RIM + 2 } Z`;

/**
 * The PLUME: a soft column leaning off the crater.
 *
 * The only thing in this whole set that is not meant to read as solid, so it is
 * drawn as overlapping round puffs and the caller paints the lot faint AND in ash
 * rather than in the mountain's own colour.
 *
 * SMALLER than the first pass, whose top puff ran sixteen units wide at y 9 — a
 * third of the box's width, sitting over a cone half its height, which is a
 * proportion no photograph of this mountain has. Smoke that outweighs the volcano
 * is not smoke.
 *
 * The bottom puff also OVERLAPS THE RIM now. It used to stop four units above it,
 * and four units of black between a crater and its own smoke is a gap — the one
 * thing this set is not allowed to ship.
 */
export const VOLCANO_PLUME = ( () => {
  const puff = ( cx: number, cy: number, r: number ): string =>
    `M${ f( cx - r ) },${ f( cy ) } a${ f( r ) },${ f( r ) } 0 1,0 ${ f( r * 2 ) },0`
    + ` a${ f( r ) },${ f( r ) } 0 1,0 ${ f( -r * 2 ) },0 Z`;
  // ROUND puffs, not ellipses. Flattened ones stack into a stratum of cloud; a
  // column of circles is what a young eruption column looks like and it is also
  // the friendlier shape, which on a LINE theme is not a small consideration.
  return [
    // FOUR small puffs rather than three big ones. Three read as a snowman: at
    // that count each ball is separately findable and the column becomes a stack
    // of objects. Four overlapping at these radii blur into one leaning shaft.
    puff( 60, 33, 6 ),
    puff( 64, 25, 7 ),
    puff( 69, 16, 8 ),
    puff( 75, 8, 8.5 ),
  ].join( ' ' );
} )();

/** The whole mountain as ONE path — cone and shoulder. This is what the idle tab
 *  and the unentered passcode dot draw: unlit, which for this subject means the
 *  mountain with nothing coming out of it. */
export const VOLCANO = `${ VOLCANO_SHOULDER } ${ VOLCANO_CONE }`;

/** The cone ALONE, for the wallpaper's smaller grains — at those sizes the
 *  shoulder is three pixels of rock beside the mountain and reads as a chip off
 *  it. */
export const VOLCANO_SIMPLE = VOLCANO_CONE;

/**
 * THE WALLPAPER'S GRAIN — the cone WITH its plume, as one flat silhouette.
 *
 * The wallpaper drew VOLCANO_CONE on its own and the owner's read was that it looked
 * careless. It did, and the diagnosis is specific rather than a matter of taste: a
 * bare cone is a flat trapezoid with a small notch in the top, which is a traffic
 * cone. Everything that says volcano — the plume, the glow at the crater, the lava —
 * lives in the OTHER paths this module exports, and the tab and the passcode dots
 * draw them in their own colours. The wallpaper draws one path in one fill, so it
 * was getting the mountain with all of its evidence removed.
 *
 * The fix is not another subject. Embers were tried on this wallpaper twice already
 * — a star that read as confetti, a teardrop that read as rain — and the lesson
 * there was that a cinder means nothing without the thing that threw it. So the
 * plume comes INTO the silhouette instead: one shape, still one fill, and a
 * mountain with something rising off its summit cannot be mistaken for road
 * furniture.
 *
 * The puffs are much larger than VOLCANO_PLUME's, and that is the whole trick. At
 * the size a grain renders, that plume's 6-to-8 unit balls are a pixel and a half
 * each and simply vanish; at 10-to-13 they survive as a widening column. It is the
 * same reason every SIMPLE export in this family is coarser than the drawing it
 * simplifies, only here the coarsening ADDS a feature rather than dropping one.
 */
export const VOLCANO_GRAIN = ( () => {
  // A widening, drifting RIBBON rather than a stack of puffs.
  //
  // Circles were the first two attempts and both failed the same way. Kept near the
  // axis at radii 10 to 12.5 the column read as a knob screwed onto the cone — a
  // chess pawn. Widened and thrown sideways it read as a chain of BEADS: three
  // discs overlap into a union whose outline still has three lobes on it, and at
  // this size lobes are all you see.
  //
  // Smoke has no lobes. A single band that starts the width of the vent and opens
  // as it rises gives one smooth edge, and one smooth edge is what survives being
  // shrunk. VOLCANO_PLUME keeps its puffs for the tab and the passcode dots, where
  // there is room for them to be individually visible and where round puffs are the
  // friendlier drawing.
  // It RISES more than it drifts, and its base starts INSIDE the cone.
  //
  // The ribbon's first spine ran 26 up against 30 sideways and started at the
  // crater lip. Both were wrong. Drifting further than it climbs makes a cap, not a
  // column — the grain came back looking like a figure in a hood — and starting at
  // the lip left the crater's own notch showing as a black slot between the two
  // shapes, so the plume read as an object balanced on the mountain rather than as
  // something coming out of it. It now climbs 37 against 18 of drift and begins at
  // y=40, well down inside the cone, where the union has no seam to show.
  const spine: [ number, number ][] = [
    [ 60, 40 ], [ 63, 28 ], [ 70, 14 ], [ 78, 3 ],
  ];
  /** Half-width along the spine: the vent's own width at the bottom, opening out. */
  const halfW = ( t: number ): number => 5.5 + Math.pow( t, 0.85 ) * 7.5;

  // sample the spine with a Catmull-Rom so the drift is a curve and not three
  // straight segments meeting at angles
  const c: [ number, number ][] = [];
  for ( let i = 0; i < spine.length - 1; i++ ) {
    const p0 = spine[ Math.max( 0, i - 1 ) ], p1 = spine[ i ];
    const p2 = spine[ i + 1 ], p3 = spine[ Math.min( spine.length - 1, i + 2 ) ];
    for ( let k = 0; k < 8; k++ ) {
      const t = k / 8, t2 = t * t, t3 = t2 * t;
      c.push( [
        0.5 * ( 2 * p1[ 0 ] + ( -p0[ 0 ] + p2[ 0 ] ) * t
          + ( 2 * p0[ 0 ] - 5 * p1[ 0 ] + 4 * p2[ 0 ] - p3[ 0 ] ) * t2
          + ( -p0[ 0 ] + 3 * p1[ 0 ] - 3 * p2[ 0 ] + p3[ 0 ] ) * t3 ),
        0.5 * ( 2 * p1[ 1 ] + ( -p0[ 1 ] + p2[ 1 ] ) * t
          + ( 2 * p0[ 1 ] - 5 * p1[ 1 ] + 4 * p2[ 1 ] - p3[ 1 ] ) * t2
          + ( -p0[ 1 ] + 3 * p1[ 1 ] - 3 * p2[ 1 ] + p3[ 1 ] ) * t3 ),
      ] );
    }
  }
  c.push( spine[ spine.length - 1 ] );

  const left: [ number, number ][] = [], right: [ number, number ][] = [];
  for ( let i = 0; i < c.length; i++ ) {
    const t = i / ( c.length - 1 );
    const q = c[ i ], r = c[ Math.min( i + 1, c.length - 1 ) ];
    const ux = r[ 0 ] - q[ 0 ], uy = r[ 1 ] - q[ 1 ];
    const d = Math.hypot( ux, uy ) || 1;
    const w = halfW( t );
    left.push( [ q[ 0 ] - uy / d * w, q[ 1 ] + ux / d * w ] );
    right.push( [ q[ 0 ] + uy / d * w, q[ 1 ] - ux / d * w ] );
  }
  // a round cap on the top, so the column ends in a billow instead of a cut edge
  const tip = c[ c.length - 1 ], w = halfW( 1 );
  const cap: [ number, number ][] = [];
  for ( let i = 1; i < 9; i++ ) {
    const a2 = -Math.PI * 0.15 + ( i / 9 ) * Math.PI * 1.3;
    cap.push( [ tip[ 0 ] + Math.cos( a2 ) * w * 0.95, tip[ 1 ] - Math.sin( a2 ) * w * 0.95 ] );
  }
  const plume = cw( left.concat( cap, [ ...right ].reverse() ) );
  return `${ VOLCANO_SHOULDER } ${ VOLCANO_CONE } ${ plume }`;
} )();

/**
 * An EMBER — one drifting cinder, the wallpaper's grain.
 *
 * A FLAME, not a droplet: a round hot body with a tip that leans off to one side.
 *
 * It replaced a four-pointed star, which read as sparkle rather than as anything
 * hot — a wallpaper of them came back looking like party confetti. The first
 * replacement was a symmetrical teardrop, and that was worse in a quieter way: a
 * round bottom under a centred point is the universal drawing of a WATER DROP, so
 * a canvas of them on black read as rain on a window. On a volcano.
 *
 * The lean is the entire fix and it costs four units. A flame is asymmetric
 * because it is being carried by the air it is burning in; a drop is symmetric
 * because it is only falling.
 */
export const EMBER =
  'M67,33'
  + ' C57,45 70,50 70,62'
  + ' a10,10 0 1,1 -20,0'
  + ' C50,51 63,46 67,33 Z';

/** A smaller, cooler cinder — the one that fills between the flames. Same lean,
 *  the other way, so a field of the two does not comb in one direction. */
export const ASH_FLECK =
  'M54,47 C60,54 66,56 66,62 a6,6 0 1,1 -12,0 C54,57 51,52 54,47 Z';

/* ------------------------------------------------------- passcode colours --
 * Held here rather than in the passcode component: line-lock (the preview the
 * marketplace screenshot shows) and line-page (the exported PNGs) both read
 * them, and every time this family kept two copies of a colour they drifted.
 */

/**
 * The four entered volcanoes, on the lock screen's near-black (#131313, sampled).
 *
 * They HEAT UP: cooled basalt, then the first red in the crust, then lava, then the
 * yellow-white at the vent. It is the theme's own two-material palette walked from
 * one end to the other, and the sampled skin left it standing — the derived zone was
 * #121111, a single unit away.
 *
 * The RAMP ITSELF was still wrong. #6E5450 measured 2.70 and #A03A40 measured 2.81,
 * a step of 1.04: two digits at the same weight are one digit twice, and the first
 * two dots were doing one dot's work. The basalt went down to #574240 and the vent
 * up to #F09A3E, which spreads the row properly.
 *
 * Measured on the near-black: 2.00 / 2.81 / 4.48 / 8.31, monotonic, each step 1.4x
 * to 1.9x the last.
 */
export const VOLCANO_PC_COLORS = [ '#574240', '#A03A40', '#D74C57', '#F09A3E' ];

/**
 * ONE tone for every unentered volcano — the frame never changes per digit (the
 * rule Aurora's orbs settled).
 *
 * 1.55:1 on the near-black. A cone with nothing coming out of it is very nearly
 * the night it is standing in, which is what Etna looks like on most nights.
 */
export const VOLCANO_PC_OFF = '#332E2E';

/**
 * A per-digit lean, alternating side, about the BASE.
 *
 * The smallest set in this run after Prague's, and for the same reason: a mountain
 * is a plumb-line object. Four leaning volcanoes is a landslide.
 */
export const VOLCANO_PC_TILTS = [ -4, 3, -2, 5 ];
